import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCalendar, FiUser, FiTag, FiClock, FiEye, FiShare2, FiBookmark, FiCheckCircle, FiAlertCircle, FiTrash2, FiX } from "react-icons/fi";
import { approvalService, statsService, revisionService } from "../../services/firestoreService";
import { useLanguage } from "../../contexts/LanguageContext";
import "../AdminPage.css";

const ContentArticleView = () => {
  const { getTranslations } = useLanguage();
  const t = getTranslations(false, true);
  const navigate = useNavigate();
  const [article, setArticle] = useState({});
  const [revision, setRevision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [articleStats, setArticleStats] = useState({ views: 0, shares: 0, bookmarks: 0 });
  const [showNotification, setShowNotification] = useState(null); // 'applied' or 'rejected'
  const [activeTab, setActiveTab] = useState('info'); // 'info', 'content', 'social'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteRevisionModal, setShowDeleteRevisionModal] = useState(false);
  const [isDeletingRevision, setIsDeletingRevision] = useState(false);

  useEffect(() => {
    // Check URL for notification parameter
    const urlParams = new URLSearchParams(window.location.search);
    const notification = urlParams.get('notification');
    if (notification) {
      setShowNotification(notification);
      // Clear the notification from URL
      const newUrl = window.location.pathname + '?id=' + urlParams.get('id');
      window.history.replaceState({}, '', newUrl);
      // Auto close notification after 3 seconds
      setTimeout(() => setShowNotification(null), 3000);
    }
  }, []);

  useEffect(() => {
    fetchArticle();
  }, []);

  const fetchStats = async (contentId) => {
    try {
      const stats = await statsService.getStats(contentId);
      setArticleStats(stats);
    } catch (error) {
      console.error('ArticleViewPage: Error fetching stats:', error);
      setArticleStats({ views: 0, shares: 0, bookmarks: 0 });
    }
  };

  const fetchArticle = async () => {
    try {
      setLoading(true);
      
      // Get article ID from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const articleId = urlParams.get('id');
      const idType = urlParams.get('idType'); // 'document' or 'content'
      
      if (!articleId) {
        setError("Article ID not provided");
        setLoading(false);
        return;
      }
      
      let data;
      
      // Use optimized logic based on ID type if specified
      if (idType === 'content') {
        data = await approvalService.getApprovalByContentId(articleId);
      } else if (idType === 'document') {
        data = await approvalService.getApprovalById(articleId);
      } else {
        // Fallback: try document ID first, then contentId if not found
        data = await approvalService.getApprovalById(articleId);
        if (!data) {
          data = await approvalService.getApprovalByContentId(articleId);
        }
      }
      
      if (data) {
        setArticle(data);
        
        // If content is approved, fetch stats from published collection
        if (data.status === "Approved" && data.contentId) {
          await fetchStats(data.contentId);
          // Check for revisions
          const existingRevision = await revisionService.getRevisionByContentId(data.contentId);
          setRevision(existingRevision);
        }
      } else {
        setError("Content not found");
      }
    } catch (err) {
      console.error("Error fetching article:", err);
      setError("Failed to load content. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    
    // Handle Firestore timestamp objects
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      // Firestore timestamp object
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      // Already a Date object
      date = dateValue;
    } else {
      // Try to parse as string
      date = new Date(dateValue);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    return (
      <>
        {dateStr}<br />{timeStr}
      </>
    );
  };

  // Format date for modals (single line)
  const formatDateModal = (dateValue) => {
    if (!dateValue) return "N/A";
    
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      date = new Date(dateValue);
    }
    
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    return `${dateStr} ${timeStr}`;
  };

  const stripHtmlToText = (html) => {
    try {
      const div = document.createElement("div");
      div.innerHTML = html || "";
      return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
    } catch {
      return "";
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = status.toLowerCase();
    return (
      <span className={`status-badge status-badge-${statusClass}`}>
        {status}
      </span>
    );
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    if (!article) return;
    try {
      setIsDeleting(true);
      await approvalService.deleteApproval(article.id);
      handleBack();
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Failed to delete content. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteRevisionClick = () => {
    setShowDeleteRevisionModal(true);
  };

  const confirmDeleteRevision = async () => {
    if (!article) return;
    try {
      setIsDeletingRevision(true);
      // Find and delete the pending revision for this content
      const revisions = await revisionService.getAllRevisions();
      const pendingRevision = revisions.find(
        rev => rev.contentId === article.contentId && rev.status === "pending"
      );
      
      if (pendingRevision) {
        await revisionService.deleteRevision(pendingRevision.id);
        // Refresh the article data to update revision status
        setRevision(null);
      } else {
        alert('No pending revision found for this content.');
      }
    } catch (error) {
      console.error('Error deleting revision:', error);
      alert('Failed to delete revision. Please try again.');
    } finally {
      setShowDeleteRevisionModal(false);
      setIsDeletingRevision(false);
    }
  };

  const closeDeleteRevisionModal = () => {
    setShowDeleteRevisionModal(false);
  };

  const handleBack = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromParam = urlParams.get('from');
    if (fromParam === 'announcements') {
      navigate('/content-curator?view=announcements');
    } else {
      // Default to library if from is not set or is library
      navigate('/content-curator?view=library');
    }
  };

  if (error || (!loading && !article)) {
    return (
      <div className="content-review-page">
        <div className="error-state">
          <h3>Error</h3>
          <p>{error || "Content not found"}</p>
          <button className="back-btn" onClick={handleBack}>
            <FiArrowLeft /> {t.back} to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-review-page">
      <style>{`
        .content-review-header::before {
          display: none !important;
        }
        .sidebar-card::before,
        .sidebar-card::after {
          display: none !important;
        }
        .content-review-page .submission-meta {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          flex-wrap: wrap;
          width: 100%;
        }
        .admin-aura-skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 4px;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .admin-aura-skeleton-text {
          height: 16px;
          margin-bottom: 8px;
        }
        .admin-aura-skeleton-title {
          height: 32px;
          width: 60%;
          margin-bottom: 16px;
        }
        .admin-aura-skeleton-image {
          width: 100%;
          height: 200px;
        }
      `}</style>
      <div className="content-review-header" style={{ 
        background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
        padding: '24px 32px',
        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.3)',
        borderBottom: 'none'
      }}>
        <div className="header-left" style={{ alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
          <button className="back-btn" onClick={handleBack}
            style={{ 
              background: 'rgba(255,255,255,0.1)', 
              color: '#fff', 
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <FiArrowLeft />
          </button>
          <div className="header-title">
            <h1 style={{ color: '#fff' }}>Article View</h1>
            <div className="submission-meta">
              <span className="submission-id-full" style={{ 
                fontSize: '14px', 
                color: '#cbd5e1', 
                fontWeight: '500', 
                padding: '8px 16px', 
                background: 'rgba(255, 255, 255, 0.15)', 
                borderRadius: '12px', 
                border: '1px solid rgba(255, 255, 255, 0.2)', 
                backdropFilter: 'blur(10px)', 
                WebkitBackdropFilter: 'blur(10px)', 
                wordBreak: 'break-word', 
                whiteSpace: 'normal', 
                maxWidth: '600px', 
                lineHeight: '1.4',
                display: 'block', 
                boxSizing: 'border-box' 
              }}>
                {loading ? (
                  <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '200px', height: '14px', background: 'rgba(255,255,255,0.2)' }} />
                ) : (
                  `Content ID: ${article.contentId || 'N/A'}`
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="content-review-body">
        <div className="content-review-main" style={{ maxWidth: '100%' }}>
          {/* Tabs Header */}
          <div style={{ 
            background: '#fff', 
            borderBottom: '1px solid #e2e8f0',
            borderRadius: '16px 16px 0 0',
            overflow: 'hidden'
          }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
              <button
                onClick={() => setActiveTab('info')}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  border: 'none',
                  background: activeTab === 'info' ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' : '#fff',
                  color: activeTab === 'info' ? '#fff' : '#64748b',
                  fontWeight: activeTab === 'info' ? 700 : 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <FiUser style={{ marginRight: '8px', display: 'inline' }} /> Submission Information
              </button>
              <button
                onClick={() => setActiveTab('content')}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  border: 'none',
                  background: activeTab === 'content' ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' : '#fff',
                  color: activeTab === 'content' ? '#fff' : '#64748b',
                  fontWeight: activeTab === 'content' ? 700 : 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <FiTag style={{ marginRight: '8px', display: 'inline' }} /> Article Content
              </button>
              <button
                onClick={() => setActiveTab('social')}
                style={{
                  flex: 1,
                  padding: '16px 24px',
                  border: 'none',
                  background: activeTab === 'social' ? 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)' : '#fff',
                  color: activeTab === 'social' ? '#fff' : '#64748b',
                  fontWeight: activeTab === 'social' ? 700 : 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <FiCalendar style={{ marginRight: '8px', display: 'inline' }} /> Social Media Preview
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div style={{ 
            background: '#fff', 
            borderRadius: '0 0 16px 16px',
            padding: '32px'
          }}>
            {/* Submission Information Tab */}
            {activeTab === 'info' && (
              <div>
                <h2 style={{ 
                  fontSize: '20px', 
                  fontWeight: 700, 
                  color: '#1e293b', 
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FiUser style={{ color: '#3f62b5' }} /> Submission Information
                </h2>
                {loading ? (
                  <div className="info-grid">
                    {[...Array(4)].map((_, i) => (
                      <div className="info-item" key={i}>
                        <label>
                          <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '120px' }} />
                        </label>
                        <div className="info-value">
                          <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${60 + (i % 3) * 20}%` }} />
                          <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${50 + (i % 4) * 15}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Submitted By</label>
                      <div className="info-value">
                        <div className="creator-name">{article.author}</div>
                        <div className="creator-email">{article.authorEmail}</div>
                        <div className="creator-role">{article.authorRole}</div>
                      </div>
                    </div>
                    <div className="info-item">
                      <label>
                        <FiCalendar /> Submitted Date
                      </label>
                      <div className="info-value">{formatDateModal(article.submittedAt)}</div>
                    </div>
                    <div className="info-item">
                      <label>Category</label>
                      <div className="info-value">{article.category}</div>
                    </div>
                    {article.minorEligible === true && (
                      <div className="info-item">
                        <label>Content Rating</label>
                        <div className="info-value">
                          <span className="minor-eligible-badge">Minor Eligible Content</span>
                        </div>
                      </div>
                    )}
                    {article.minorEligible === false && (
                      <div className="info-item">
                        <label>Content Rating</label>
                        <div className="info-value">
                          <span className="not-minor-eligible-badge">Not Minor Eligible Content</span>
                        </div>
                      </div>
                    )}
                    {article.status === "Approved" && (
                      <div className="info-item">
                        <label>
                          <FiClock /> Approved Date
                        </label>
                        <div className="info-value">{formatDate(article.approvedAt)}</div>
                      </div>
                    )}
                    {article.status === "Rejected" && (
                      <div className="info-item">
                        <label>
                          <FiClock /> Rejected Date
                        </label>
                        <div className="info-value">{formatDate(article.rejectedAt)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Article Content Tab */}
            {activeTab === 'content' && (
              <div>
                <h2 style={{ 
                  fontSize: '20px', 
                  fontWeight: 700, 
                  color: '#1e293b', 
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FiTag style={{ color: '#3f62b5' }} /> Article Content
                </h2>
                {loading ? (
                  <div className="article-content">
                    <div className="article-header">
                      <div className="admin-aura-skeleton admin-aura-skeleton-title" />
                      <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '70%' }} />
                    </div>
                    <div className="article-body">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${80 + (i % 3) * 10}%`, marginBottom: '12px' }} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="article-content">
                    <div className="article-header">
                      <h1 className="article-title">{article.title}</h1>
                      {article.subtitle && (
                        <h2 className="article-subtitle">{article.subtitle}</h2>
                      )}
                    </div>

                    {/* Tags */}
                    {article.tags && article.tags.length > 0 && (
                      <div className="article-tags">
                        <FiTag />
                        {article.tags.map((tag, index) => (
                          <span key={index} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}

                    {/* Article Body */}
                    <div className="article-body">
                      <div 
                        className="article-content-html"
                        dangerouslySetInnerHTML={{ __html: article.bodyHtml }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Social Media Preview Tab */}
            {activeTab === 'social' && (
              <div>
                <h2 style={{ 
                  fontSize: '20px', 
                  fontWeight: 700, 
                  color: '#1e293b', 
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FiCalendar style={{ color: '#3f62b5' }} /> Social Media Preview
                </h2>
                {loading ? (
                  <div className="social-preview-card">
                    <div className="social-image">
                      <div className="admin-aura-skeleton admin-aura-skeleton-image" />
                    </div>
                    <div className="social-content">
                      <div className="social-title">
                        <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '80%' }} />
                      </div>
                      <div className="social-description">
                        <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '100%' }} />
                        <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '90%' }} />
                      </div>
                      <div className="social-domain">
                        <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '60px' }} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="social-preview-card">
                    {article.socialImageUrl && (
                      <div className="social-image">
                        <img src={article.socialImageUrl} alt="Social preview" />
                      </div>
                    )}
                    <div className="social-content">
                      <div className="social-title">
                        {article.socialTitle || article.title}
                      </div>
                      <div className="social-description">
                        {article.socialDescription || stripHtmlToText(article.bodyHtml).substring(0, 160)}
                      </div>
                      <div className="social-domain">auravit</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Rejection Reason (shown in all tabs if applicable) */}
            {article.status === "Rejected" && article.rejectionReason && (
              <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                <h2 style={{ 
                  fontSize: '20px', 
                  fontWeight: 700, 
                  color: '#dc2626', 
                  marginBottom: '16px'
                }}>
                  Rejection Reason
                </h2>
                <div className="rejection-reason">
                  <p>{article.rejectionReason}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Status Only */}
        <aside className="content-review-sidebar">
          <div className="sidebar-card" style={{ borderRadius: '16px', overflow: 'hidden', padding: 0 }}>
            <h3 style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
              color: '#ffffff !important',
              padding: '16px 20px',
              margin: 0,
              fontSize: '16px',
              fontWeight: 700,
              WebkitTextFillColor: '#ffffff',
              textAlign: 'left',
              width: '100%'
            }}>
              Content Status
            </h3>
            <div style={{ padding: '20px' }}>
            <div className="status-info" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: '#f1f5f9', borderRadius: '12px' }}>
                {loading ? (
                  <>
                    <div className="admin-aura-skeleton" style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
                    <div>
                      <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '90px' }} />
                      <div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '160px', marginBottom: 0 }} />
                    </div>
                  </>
                ) : (
                  <>
                    {article.status === "Approved" ? (
                      <FiCheckCircle style={{ fontSize: '24px', color: '#10b981' }} />
                    ) : article.status === "Rejected" ? (
                      <FiAlertCircle style={{ fontSize: '24px', color: '#ef4444' }} />
                    ) : (
                      <FiClock style={{ fontSize: '24px', color: '#f59e0b' }} />
                    )}
                    <div>
                      {getStatusBadge(article.status)}
                      <p className="status-description" style={{ marginTop: '4px', marginBottom: 0, fontSize: '13px' }}>
                        {article.status === "Pending" && "This content is currently pending review."}
                        {article.status === "Approved" && "This content has been approved and published."}
                        {article.status === "Rejected" && "This content has been rejected."}
                        {article.status === "draft" && "This is a draft that has not been submitted."}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            {(article.status === "Pending" || article.status === "Approved") && (
              <button
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  marginTop: '16px',
                  background: revision?.status === "pending" ? '#9ca3af' : 'linear-gradient(135deg, #3f62b5 0%, #2563eb 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: revision?.status === "pending" ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  opacity: revision?.status === "pending" ? 0.6 : 1,
                  boxShadow: revision?.status === "pending" ? 'none' : '0 4px 12px rgba(63, 98, 181, 0.3)'
                }}
                onClick={() => {
                  if (revision?.status !== "pending") {
                    navigate(`/curator-revise/${article.contentId || article.id}`);
                  }
                }}
                disabled={revision?.status === "pending"}
              >
                {revision?.status === "pending" ? "Revision Pending" : "Revise"}
              </button>
            )}
            
            {revision?.status === "pending" && (
              <button
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  marginTop: '12px',
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)'
                }}
                onClick={() => navigate(`/view-revisions/${article.contentId}`)}
              >
                View Revisions
              </button>
            )}

            {revision?.status === "pending" && (
              <button
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  marginTop: '12px',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onClick={handleDeleteRevisionClick}
                disabled={isDeletingRevision}
              >
                {isDeletingRevision ? 'Deleting...' : 'Delete Revision'}
              </button>
            )}

            {article.status === "Rejected" && (
              <button
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  marginTop: '16px',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onClick={handleDeleteClick}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Content'}
              </button>
            )}
            </div>
          </div>

          {/* Metrics Container - Only show for approved content */}
          {article.status === "Approved" && (
            <div className="sidebar-card" style={{ borderRadius: '16px', overflow: 'hidden', padding: 0 }}>
              <h3 style={{ 
                background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                color: '#ffffff !important',
                padding: '16px 20px',
                margin: 0,
                fontSize: '16px',
                fontWeight: 700,
                WebkitTextFillColor: '#ffffff',
                textAlign: 'left',
                width: '100%'
              }}>
                Content Engagement
              </h3>
              <div style={{ padding: '20px' }}>
              <div className="metrics-info" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="metric-item" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: '#f1f5f9',
                  borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FiEye style={{ fontSize: '20px', color: '#3f62b5' }} />
                    <div className="metric-label" style={{ marginBottom: 0 }}>Views</div>
                  </div>
                  <div className="metric-value" style={{ color: '#3f62b5', fontWeight: 700, fontSize: '18px' }}>{articleStats.views}</div>
                </div>
                <div className="metric-item" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: '#f1f5f9',
                  borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FiShare2 style={{ fontSize: '20px', color: '#3f62b5' }} />
                    <div className="metric-label" style={{ marginBottom: 0 }}>Shares</div>
                  </div>
                  <div className="metric-value" style={{ color: '#3f62b5', fontWeight: 700, fontSize: '18px' }}>{articleStats.shares}</div>
                </div>
                <div className="metric-item" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: '#f1f5f9',
                  borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <FiBookmark style={{ fontSize: '20px', color: '#3f62b5' }} />
                    <div className="metric-label" style={{ marginBottom: 0 }}>Bookmarks</div>
                  </div>
                  <div className="metric-value" style={{ color: '#3f62b5', fontWeight: 700, fontSize: '18px' }}>{articleStats.bookmarks}</div>
                </div>
              </div>
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* Notification Modal */}
      {showNotification && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          padding: '30px 50px',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          zIndex: 1000,
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '15px',
            color: showNotification === 'applied' ? '#10b981' : '#ef4444'
          }}>
            {showNotification === 'applied' ? '✓ Revisions Applied' : '✕ Revisions Rejected'}
          </div>
          <div style={{
            fontSize: '16px',
            color: '#475569'
          }}>
            {showNotification === 'applied' 
              ? 'The proposed revisions have been successfully applied.' 
              : 'The proposed revisions have been rejected.'}
          </div>
        </div>
      )}

      {/* Overlay */}
      {showNotification && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 999
        }} />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} onClick={handleDeleteModalClose}>
          </div>
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            zIndex: 1001,
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center'
          }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={handleDeleteModalClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#64748b',
                padding: '4px'
              }}
            >
              <FiX />
            </button>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <FiTrash2 size={32} color="#dc2626" />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#1e293b'
            }}>
              Delete Content
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              marginBottom: '20px'
            }}>
              Are you sure you want to delete this content? This action cannot be undone.
            </p>
            <div style={{
              background: '#f9fafb',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>{article.title}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Submitted Date:</strong> {formatDateModal(article.submittedAt || article.createdAt)}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Category:</strong> {article.category || 'Uncategorized'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleDeleteModalClose}
                style={{
                  padding: '12px 24px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                style={{
                  padding: '12px 24px',
                  background: '#fee2e2',
                  border: '1px solid #fee2e2',
                  borderRadius: '8px',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#dc2626',
                  transition: 'all 0.2s ease',
                  opacity: isDeleting ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting) {
                    e.target.style.backgroundColor = '#dc2626';
                    e.target.style.borderColor = '#dc2626';
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting) {
                    e.target.style.backgroundColor = '#fee2e2';
                    e.target.style.borderColor = '#fee2e2';
                    e.target.style.color = '#dc2626';
                  }
                }}
              >
                {isDeleting ? 'Deleting...' : <><FiTrash2 style={{ marginRight: '4px' }} /> Delete</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Revision Confirmation Modal */}
      {showDeleteRevisionModal && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} onClick={closeDeleteRevisionModal}>
          </div>
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
            zIndex: 1001,
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center'
          }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeDeleteRevisionModal}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#64748b',
                padding: '4px'
              }}
            >
              <FiX />
            </button>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <FiTrash2 size={32} color="#dc2626" />
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#1e293b'
            }}>
              Delete Revision
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              marginBottom: '20px'
            }}>
              Are you sure you want to delete this revision? This action cannot be undone.
            </p>
            <div style={{
              background: '#f9fafb',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '1px solid #e5e7eb',
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>{article.title}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Category:</strong> {article.category || 'Uncategorized'}
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  <strong>Submitted Date:</strong> {formatDateModal(revision?.createdAt || article.submittedAt || article.createdAt)}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={closeDeleteRevisionModal}
                style={{
                  padding: '12px 24px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteRevision}
                disabled={isDeletingRevision}
                style={{
                  padding: '12px 24px',
                  background: '#fee2e2',
                  border: '1px solid #fee2e2',
                  borderRadius: '8px',
                  cursor: isDeletingRevision ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#dc2626',
                  transition: 'all 0.2s ease',
                  opacity: isDeletingRevision ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDeletingRevision) {
                    e.target.style.backgroundColor = '#dc2626';
                    e.target.style.borderColor = '#dc2626';
                    e.target.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeletingRevision) {
                    e.target.style.backgroundColor = '#fee2e2';
                    e.target.style.borderColor = '#fee2e2';
                    e.target.style.color = '#dc2626';
                  }
                }}
              >
                {isDeletingRevision ? 'Deleting...' : <><FiTrash2 style={{ marginRight: '4px' }} /> Delete Revision</>}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ContentArticleView;
